/* eslint-disable react/no-unused-prop-types */
import React, { useImperativeHandle, memo, forwardRef, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@material-ui/core/styles'
import styled from 'styled-components'

import { AlertSuccess, AlertInfo, AlertError, AlertWarning, Close } from '../icons/svg'
import { toastZIndex } from '../common'
import { useMergeState } from '../hooks'
import { clearBlocking, setBlocking } from './'
import { translate } from '../multilanguage'

export const defaultAutoHideDuration = 4000
const toastMessagePadding = '10px 10px 10px 0px'
const titlePadding = '10px'
const toastTop = '20px'
const toastLRPadding = '20px'
const iconContainerWidth = '60px'
const toastBorderRadius = '4px'

// Define the redux initial state at the reducer key
const initialState = {
  noTranslateTitle: false,
  noTranslateMessage: false,
  toastOn: false,
  isBlocking: undefined,
  autoHideDuration: null,
  title: '',
  message: '',
  severity: ''
}

const BaseToastContainer = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: ${toastZIndex + 1};
  top: 0px;
  left: 0px;
`

const BaseToastBodyContainer = styled.div`
  padding-top: 10px;
  top: ${toastTop};
  left: ${toastLRPadding};
  width: calc(100% - 2 * ${toastLRPadding});
  display: flex;
  flex-direction: row;
  box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
  background-color: ${props => props.backgroundColor};
  color: ${props => props.color};
  border-radius: ${toastBorderRadius};
  cursor: pointer;
  z-index: ${toastZIndex + 2};
  position: sticky;
`

const ToastIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  background-color: ${props => props.backgroundIcon};
  width: ${iconContainerWidth};
`

const CloseIconContainer = styled.div`
  margin-right: 10px;
`

const ToastTitleMessageContainer = styled.div`
  width: 100%;
  padding: ${toastMessagePadding};
  padding-top: 0px;
`

const ToastTitleContainer = styled.div`
  font-weight: ${props => props.theme.typography.fontWeightBold};
  font-size: ${props => props.theme.typography.toast.fontSizeTitle};
  padding-bottom: ${titlePadding};
`

const ToastMessageContainer = styled.div`
  font-size: ${props => props.theme.typography.toast.fontSizeMessage};
`

/**
 * Implements a toast. The component must be included in the render part your page and set a ref to it to call the toast display functions.
 *
 * @returns {LocalToast} LocalToast component
 */
const LocalToast = memo(forwardRef((_props, ref) => {
  const [, setState, getState] = useMergeState({ toast: initialState })
  const id = useRef(null)
  const theme = useTheme()
  const { toast } = getState()
  const toastId = toast.id || 'LocalToast'
  const color = theme.palette.toast.color
  let backgroundColor
  let icon
  switch (toast.severity) {
    case 'success' :
      backgroundColor = theme.palette.toast.backgroundSuccess
      icon = <AlertSuccess />
      break
    case 'warning' :
      backgroundColor = theme.palette.toast.backgroundWarning
      icon = <AlertWarning />
      break
    case 'info' :
      backgroundColor = theme.palette.toast.backgroundInfo
      icon = <AlertInfo />
      break
    case 'error' :
      backgroundColor = theme.palette.toast.backgroundError
      icon = <AlertError />
      break
  }

  const setToastOff = () => {
    if (!getState().toast.toastOn) {
      return
    }
    if (getState().toast.isBlocking) {
      clearBlocking()
    }
    setState({ toast: initialState })
  }

  useEffect(() => {
    return setToastOff
  }, [])

  const setToastOn = toastObj => {
    if (id.current) {
      clearTimeout(id.current)
      id.current = null
      setToastOff()
    }
    if (toastObj.isBlocking) {
      setBlocking()
    }
    setState({ toast: { ...initialState, ...toastObj, toastOn: true, noTranslateMessage: true, noTranslateTitle: true } })
    if (getState().toast.autoHideDuration) {
      id.current = setTimeout(setToastOff, getState().toast.autoHideDuration)
    } else {
      id.current = null
    }
  }

  useImperativeHandle(ref, () => ({
    setToastOff: () => {
      setToastOff()
    },
    successToastOn: toastObj => {
      setToastOn({
        ...toastObj,
        severity: 'success',
        autoHideDuration: toastObj.autoHideDuration === undefined ? defaultAutoHideDuration : toastObj.autoHideDuration,
        isBlocking: toastObj.isBlocking === undefined ? false : toastObj.isBlocking
      })
    },
    warningToastOn: toastObj => {
      setToastOn({
        ...toastObj,
        severity: 'warning',
        autoHideDuration: toastObj.autoHideDuration,
        isBlocking: toastObj.isBlocking === undefined ? true : toastObj.isBlocking
      })
    },
    infoToastOn: toastObj => {
      setToastOn({
        ...toastObj,
        severity: 'info',
        autoHideDuration: toastObj.autoHideDuration,
        isBlocking: toastObj.isBlocking === undefined ? true : toastObj.isBlocking
      })
    },
    errorToastOn: toastObj => {
      setToastOn({
        ...toastObj,
        severity: 'error',
        autoHideDuration: toastObj.autoHideDuration,
        isBlocking: toastObj.isBlocking === undefined ? true : toastObj.isBlocking
      })
    }
  }))

  const onClick = () => {
    setToastOff()
  }

  if (!toast.toastOn) {
    return null
  }
  return (
    <BaseToastContainer>
      <BaseToastBodyContainer id={toastId} onClick={onClick} color={color} backgroundColor={backgroundColor}>
        <ToastIconContainer>
          {icon}
        </ToastIconContainer>
        <ToastTitleMessageContainer id={`${toastId}-title`} theme={theme}>
          {toast.title
            ? <ToastTitleContainer theme={theme}>
              {toast.noTranslateTitle ? toast.title : translate(toast.title)}
            </ToastTitleContainer>
            : null
          }
          <ToastMessageContainer id={`${toastId}-message`} theme={theme}>
            {toast.noTranslateMessage ? toast.message : translate(toast.message)}
          </ToastMessageContainer>
        </ToastTitleMessageContainer>
        <CloseIconContainer>
          <Close scale={0.8} />
        </CloseIconContainer>
      </BaseToastBodyContainer>
    </BaseToastContainer>
  )
}))

LocalToast.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string, 
  /**
   Toast title.
   */
  title: PropTypes.string,
  /**
   Toast message.
   */
  message: PropTypes.string,
  /**
      Prevents the language translation of the message.
   */
  noTranslateMessage: PropTypes.bool,
  /**
   Prevents the language translation of the title.
   */
  noTranslateTitle: PropTypes.bool,
  /**
   Blocks the screen while the toast is displayed to prevent any user activity.
   */
  isBlocking: PropTypes.bool,
  /**
   Milliseconds at which the toast is automatically removed from the screen.
   */
  autoHideDuration: PropTypes.number,
  /**
   Set a ref to the LocalToast component in your page and call this function to display a success toast.<br/>
   Use the above props in an object as a parameter to the function call in order to configure the toast.<br/>
   Defaults autoHideDuration: 4000<br/>
   Defaults isBlocking: false
   */
  'ref.current.successToastOn': PropTypes.func,
  /**
   Set a ref to the LocalToast component in your page and call this function to display an error toast.<br/>
   Use the above props in an object as a parameter to the function call in order to configure the toast.<br/>
   Defaults isBlocking: true
   */
  'ref.current.errorToastOn': PropTypes.func,
  /**
   Set a ref to the LocalToast component in your page and call this function to display an information toast.<br/>
   Use the above props in an object as a parameter to the function call in order to configure the toast.<br/>
   Defaults isBlocking: true
   */
  'ref.current.infoToastOn': PropTypes.func,
  /**
   Set a ref to the LocalToast component in your page and call this function to display a warning toast.<br/>
   Use the above props in an object as a parameter to the function call in order to configure the toast.<br/>
   Defaults isBlocking: true
   */
  'ref.current.warningToastOn': PropTypes.func,
}

LocalToast.defaultProps = {
  noTranslateMessage: false,
  noTranslateTitle: false,
  toastOn: false,
  isBlocking: false,
  autoHideDuration: null,
  title: '',
  message: ''
}

export default LocalToast